import { catalogDetailPath, getCatalogCapabilities, getCatalogItemKind } from "./catalogRouting";
import { getProductId } from "./productAdapter";

const DEFAULTS = Object.freeze({
  gap: 18,
  radius: 16,
  backgroundColor: "",
  backgroundOpacity: 1,
  backgroundImageUrl: "",
  backgroundImagePosition: "center",
  backgroundImageFit: "cover",
  backgroundImageOpacity: 0.28,
  imagePosition: "top",
  imageFit: "cover",
  imageHeight: 210,
  showImage: true,
  showTitle: true,
  showButton: true,
  showType: true,
  showCategory: true,
  showSubcategory: true,
  showDescription: true,
  showPrice: true,
  showDuration: true,
  showCapabilities: true,
  showQuickCart: true,
  borderColor: "",
  shadowColor: "",
  typeColor: "",
  titleColor: "",
  categoryColor: "",
  subcategoryColor: "",
  descriptionColor: "",
  priceColor: "",
  buttonTextColor: "",
  buttonBackgroundColor: "",
  buttonHoverTextColor: "",
  buttonHoverBackgroundColor: "",
  ctaLabel: "",
  serviceCtaLabel: "",
  ctaBehavior: "detail",
  ctaUrl: "",
});

function numberInRange(value, fallback, min, max) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.min(max, Math.max(min, parsed)) : fallback;
}

function hasOwn(value, key) {
  return Boolean(value && Object.prototype.hasOwnProperty.call(value, key));
}

const SHADOW_LIMITS = Object.freeze({
  shadowOpacity: [0, 1],
  shadowX: [-100, 100],
  shadowY: [-100, 100],
  shadowBlur: [0, 160],
  shadowSpread: [-80, 80],
});

const SHADOW_FALLBACK = Object.freeze({
  shadowOpacity: 1,
  shadowX: 0,
  shadowY: 8,
  shadowBlur: 24,
  shadowSpread: 0,
});

function normalizedOptionalShadow(source) {
  const output = {};
  Object.entries(SHADOW_LIMITS).forEach(([key, [min, max]]) => {
    if (!hasOwn(source, key) || source[key] === "" || source[key] === null || source[key] === undefined) return;
    output[key] = numberInRange(source[key], SHADOW_FALLBACK[key], min, max);
  });
  return output;
}

function shadowGeometryConfigured(config) {
  return Object.keys(SHADOW_LIMITS).some((key) =>
    hasOwn(config, key) && config[key] !== "" && config[key] !== null && config[key] !== undefined
  );
}

function cssColorWithOpacity(color, opacity) {
  const candidate = String(color || "#000000").trim() || "#000000";
  const alpha = numberInRange(opacity, 1, 0, 1);
  if (alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;
  const percentage = Math.round(alpha * 1000) / 10;
  return `color-mix(in srgb, ${candidate} ${percentage}%, transparent)`;
}

export function normalizeCatalogCardConfig(value) {
  const source = value && typeof value === "object" ? value : {};
  return {
    ...DEFAULTS,
    ...source,
    gap: numberInRange(source.gap, DEFAULTS.gap, 0, 64),
    radius: numberInRange(source.radius, DEFAULTS.radius, 0, 48),
    backgroundOpacity: numberInRange(source.backgroundOpacity, DEFAULTS.backgroundOpacity, 0, 1),
    backgroundImageOpacity: numberInRange(source.backgroundImageOpacity, DEFAULTS.backgroundImageOpacity, 0, 1),
    backgroundImagePosition: ["center", "top", "bottom", "left", "right"].includes(source.backgroundImagePosition)
      ? source.backgroundImagePosition
      : DEFAULTS.backgroundImagePosition,
    backgroundImageFit: ["cover", "contain"].includes(source.backgroundImageFit)
      ? source.backgroundImageFit
      : DEFAULTS.backgroundImageFit,
    imageHeight: numberInRange(source.imageHeight, DEFAULTS.imageHeight, 90, 420),
    imagePosition: ["top", "left", "right"].includes(source.imagePosition) ? source.imagePosition : DEFAULTS.imagePosition,
    imageFit: ["cover", "contain"].includes(source.imageFit) ? source.imageFit : DEFAULTS.imageFit,
    ctaBehavior: source.ctaBehavior === "custom" ? "custom" : "detail",
    ...normalizedOptionalShadow(source),
  };
}

export function catalogCardBoxShadow(config, { legacyY = 8, legacyBlur = 24, fallback } = {}) {
  if (!shadowGeometryConfigured(config)) {
    return config?.shadowColor ? `0 ${legacyY}px ${legacyBlur}px ${config.shadowColor}` : fallback;
  }
  const opacity = numberInRange(config?.shadowOpacity, SHADOW_FALLBACK.shadowOpacity, 0, 1);
  if (opacity <= 0) return "none";
  const x = numberInRange(config?.shadowX, SHADOW_FALLBACK.shadowX, -100, 100);
  const y = numberInRange(config?.shadowY, SHADOW_FALLBACK.shadowY, -100, 100);
  const blur = numberInRange(config?.shadowBlur, SHADOW_FALLBACK.shadowBlur, 0, 160);
  const spread = numberInRange(config?.shadowSpread, SHADOW_FALLBACK.shadowSpread, -80, 80);
  const color = cssColorWithOpacity(config?.shadowColor || "#000000", opacity);
  return `${x}px ${y}px ${blur}px ${spread}px ${color}`;
}

export function catalogCardImagePosition(config) {
  if (config?.imagePosition === "left") return "left center";
  if (config?.imagePosition === "right") return "right center";
  return "center top";
}

export function catalogCardTarget(product, config) {
  const custom = String(config?.ctaUrl || "").trim();
  if (config?.ctaBehavior === "custom" && custom) {
    const id = encodeURIComponent(getProductId(product) || "");
    const type = encodeURIComponent(getCatalogItemKind(product) || "product");
    return custom.replaceAll("{id}", id).replaceAll("{type}", type);
  }
  return catalogDetailPath(product);
}

export function catalogCardCtaLabel(product, config, fallback = "") {
  const capabilities = getCatalogCapabilities(product);
  const type = getCatalogItemKind(product);
  if (type === "service" || type === "hybrid") {
    const serviceLabel = String(config?.serviceCtaLabel || "").trim();
    if (serviceLabel) return serviceLabel;
    if (capabilities.reservable) return "Reservar";
  }
  return String(config?.ctaLabel || fallback || "").trim();
}
